import { useRef, useState } from "react";

import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { twMerge } from "@/utils/twMerge";

import type { ChangeEvent, ComponentPropsWithoutRef, KeyboardEvent } from "react";

type Value = number | string;

export type Props = Omit<ComponentPropsWithoutRef<"div">, "onSubmit"> & {
  value?: Value;
  min?: number;
  max?: number;
  icon?: React.ReactNode;
  placeholder?: string;
  incrementFrom?: number;
  renderButtons?: boolean;
  inputClassName?: string;
  onSubmit: (value: number) => void;
};

export function NumberInput({
  value,
  min = -Infinity,
  max = Infinity,
  icon,
  onSubmit,
  className,
  placeholder,
  incrementFrom,
  renderButtons = true,
  inputClassName,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  ...props
}: Props) {
  const [inputValue, setInputValue] = useState<Value | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("sidebar");

  function selectInput() {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    });
  }

  function normalizeValue(val: Value | undefined): number | undefined {
    if (val === undefined || val === "") {
      return undefined;
    }

    const num = Number(val);

    if (Number.isNaN(num)) {
      return undefined;
    }

    const intValue = Math.round(num);
    return Math.min(Math.max(intValue, min), max);
  }

  const submitValue = (newValue: number | undefined) => {
    if (newValue !== undefined && newValue !== value) {
      onSubmit(newValue);
    }

    setInputValue(undefined);
  };

  const handleIncrement = (increment: number) => {
    const currentValue = inputValue ?? value;
    let baseValue = normalizeValue(currentValue) ?? min;

    if (!currentValue && incrementFrom !== undefined) {
      baseValue = incrementFrom;
    }

    const newValue = baseValue + increment;
    const normalizedValue = normalizeValue(newValue);

    if (normalizedValue !== undefined) {
      submitValue(normalizedValue);
    }
  };

  const handleOnBlur = () => {
    const normalizedValue = normalizeValue(inputValue);
    submitValue(normalizedValue);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const multiplier = e.shiftKey ? 10 : 1;

    switch (e.code) {
      case "Enter": {
        e.stopPropagation();

        const normalizedValue = normalizeValue(inputValue);

        submitValue(normalizedValue);
        selectInput();
        break;
      }

      case "ArrowUp": {
        e.preventDefault();
        handleIncrement(multiplier);
        selectInput();
        break;
      }

      case "ArrowDown": {
        e.preventDefault();
        handleIncrement(-multiplier);
        selectInput();
        break;
      }

      default: {
        break;
      }
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const currentValue = inputValue ?? value;
  const incrementDisabled =
    currentValue === max || (incrementFrom !== undefined && incrementFrom >= max);
  const decrementDisabled =
    currentValue === min || (incrementFrom !== undefined && incrementFrom <= min);

  return (
    <div role="group" className={twMerge("flex relative h-[33px]", className)} {...props}>
      {icon && (
        <div className="flex items-center absolute left-0 top-0 h-full pl-1 pointer-events-none">
          {icon}
        </div>
      )}
      <input
        ref={inputRef}
        value={currentValue}
        placeholder={placeholder}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onBlur={handleOnBlur}
        onFocus={selectInput}
        className={twMerge(
          "text-sm h-full w-full p-1.5 bg-input-bg shadow-block dark:shadow-block-inverted rounded-6",
          icon && "pl-8",
          renderButtons && "pr-5",
          inputClassName,
        )}
        inputMode="numeric"
        spellCheck="false"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      />
      {renderButtons && (
        <div className="flex flex-col justify-between p-0.5 absolute top-0 right-0 h-full">
          <Sidebar.Button
            inverted
            disabled={incrementDisabled}
            icon={<Icon name="ChevronDown" className="rotate-180" />}
            aria-label={t("increment")}
            className="w-4.5 h-3.5 min-h-3.5 bg-sidebar-button-hover-bg rounded-4 overflow-hidden"
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleIncrement(1);
            }}
          />
          <Sidebar.Button
            inverted
            disabled={decrementDisabled}
            icon={<Icon name="ChevronDown" />}
            aria-label={t("decrement")}
            className="w-4.5 h-3.5 min-h-3.5 bg-sidebar-button-hover-bg rounded-4 overflow-hidden"
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleIncrement(-1);
            }}
          />
        </div>
      )}
    </div>
  );
}
